// src/components/auth/InvestorPortalLink.tsx
/**
 * "Investor? Go to the investor portal" (MPPI-00276).
 *
 * The mirror of `AdminPortalLink` on the investor side. The two portals share
 * a brand and a look, and they authenticate against DIFFERENT Cognito clients,
 * so an investor who lands here gets an invalid-credentials error that points
 * at their password rather than at the door.
 *
 * Colours are inverted relative to the admin chrome — gold ground, navy text
 * and mark — for the same reason the admin button is navy on the investor
 * portal: it should look like the place it goes to, not like another control
 * belonging to the page it sits on.
 *
 * The mark comes from `brand.favicon_url`, so each tenant shows its own rather
 * than one brand's artwork appearing on another's sign-in page.
 */
import { useBrandingStore } from "@/store/brandingStore";

export function InvestorPortalLink() {
  const brand = useBrandingStore((s) => s.branding);
  const href = brand.portal_url;
  if (!href) return null;

  // Same-origin path, not the API asset URL. A cross-origin image is not
  // CORS-clean for `mask-image`, so the browser silently renders NO mask and
  // the icon simply never appears -- which is exactly what happened, with no
  // console error to point at it. The tenant's document root already holds
  // that tenant's own icons (build_brand_root writes them), so this stays
  // per-brand without reaching across origins.
  const mark = "/favicon-192x192.png";

  return (
    <div className="mt-6 border-t border-gray-200 pt-5">
      <a
        href={href}
        className="flex w-full items-center justify-center gap-2.5 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-accent-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
      {/* The mark is MASKED rather than shown as an <img>, so it takes the
          button's own text colour: gold here, navy on the investor button.
          Since the favicon became a single-colour mark on a transparent
          ground, an <img> rendered it navy-on-navy and it disappeared.
          Masking also keeps this brand-neutral -- no artwork is hardcoded,
          and a tenant's own mark is what gets recoloured. */}
        {(
          <span
            aria-hidden
            className="h-5 w-5 shrink-0 bg-current"
            style={{
              maskImage: `url(${mark})`,
              WebkitMaskImage: `url(${mark})`,
              maskSize: "contain",
              WebkitMaskSize: "contain",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskPosition: "center",
            }}
          />
        )}
        <span>{brand.company_name} Investor Portal</span>
      </a>
      <p className="mt-2 text-center text-[11px] text-gray-400">
        Investors and clients sign in there — this page is for staff.
      </p>
    </div>
  );
}
